import { Check } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { type RolloutRow, blockReason } from '../../utils/contractRollout'

// Renders the action affordance for one driver row. Nothing is shown once the
// contract is signed, or when the viewer lacks send rights. Excluded / no-email
// drivers get a disabled button whose title explains why.
export function RolloutAction({
  row,
  canSend,
  busy,
  justSent,
  onSend,
  onResend,
}: {
  row: RolloutRow
  canSend: boolean
  busy: boolean
  justSent: boolean
  onSend: (row: RolloutRow) => void
  onResend: (row: RolloutRow) => void
}) {
  if (row.new_contract_signed) return null
  if (!canSend) return null

  if (row.contract_blocked || row.blocked_no_email) {
    const reason = blockReason(row)
    return (
      <span title={reason} className="inline-block">
        <Button
          size="sm"
          disabled
          title={reason}
          className="h-9 w-full justify-center sm:w-auto"
        >
          Send new contract
        </Button>
      </span>
    )
  }

  // The primary button is deliberately dead once the contract is out, so a
  // second click cannot fire another send; resending is still possible but
  // demoted to a small secondary link that has to be aimed at.
  if (justSent || (row.in_scope && row.contract_status === 'sent')) {
    return (
      <div className="flex flex-col items-start gap-1 sm:items-end">
        <Button
          size="sm"
          variant="outline"
          disabled
          aria-disabled="true"
          className="h-9 w-full cursor-not-allowed justify-center opacity-100 sm:w-auto"
        >
          <Check className="h-4 w-4" />
          Contract sent
        </Button>
        <button
          type="button"
          onClick={() => onResend(row)}
          disabled={busy}
          className="text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground disabled:opacity-50"
        >
          {busy ? 'Resending…' : 'Resend'}
        </button>
      </div>
    )
  }

  if (row.in_scope) {
    return (
      <Button
        size="sm"
        onClick={() => onSend(row)}
        disabled={busy}
        className="h-9 w-full justify-center sm:w-auto"
      >
        {busy && <Spinner className="h-4 w-4" />}
        Send new contract
      </Button>
    )
  }

  return null
}
